<template>
  <div
    id="map-wrap"
    :class="[
      $style['map'],
      { [$style['map--height--' + height]]: height },
      { [$style['map--store']]: store }
    ]">
    <client-only>
      <l-map
        ref="map"
        :class="$style['vue2leaflet-map']"
        :zoom="zoom"
        :use-global-leaflet="false"
        :center="center">
        <l-tile-layer
          :max-zoom="50"
          :min-zoom="7"
          url="https://api.mapbox.com/styles/v1/hendriksenremco/cl6g7dqb7002l14m92phfi47a/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoiaGVuZHJpa3NlbnJlbWNvIiwiYSI6ImNsNmc2eTdseDBvM3UzZXByOHJqZjlsNTQifQ.48MwiuYwhRvtfN3GumKyqw" />
        <l-marker
          v-for="(location, index) in props.stores"
          ref="locations"
          :key="index"
          :lat-lng="[location.geolocation.lat, location.geolocation.long]"
          @ready="openPopup($event, location.name)"
          @click="
            zoomAndShow(location.geolocation.lat, location.geolocation.long)
          ">
          <l-icon
            :icon-size="iconSize"
            :icon-anchor="[iconSize[0] / 2, iconSize[1]]"
            :icon-url="bearIcon" />
          <l-popup
            ref="popup"
            :options="{
              offset: [0, -60]
            }"
            :class="$style['vue2leaflet-map__popup']"
            @remove="zoomOut">
            <img
              v-if="!store"
              format="webp"
              :class="$style['vue2leaflet-map__popup__image']"
              sizes="100vw"
              :src="`${config.public.cdnURL}pictures/shops/medium/${location.name}.webp`">
            <div
              :class="[
                $style['vue2leaflet-map__popup__text'],
                { [$style['vue2leaflet-map__popup__text--store-detail']]: storeDetail }
              ]">
              <div>
                <h4>{{ location.title }}</h4>
                {{ location.address.street }}
                {{ location.address.housenr }} <br>
                {{ location.address.postalcode }}
              </div>
              <StoreStatus
                :class="$style['vue2leaflet-map__popup__status']"
                :store-name="location.name"
                :opening-hours="location.openingHours"
                :special-opens-at="location.specialOpensAt" />
            </div>
            <div :class="$style['vue2leaflet-map__popup__buttons']">
              <btn
                v-if="!store"
                color="cta"
                :to="`/${$lang}/stores/${location.slug}`">
                {{ $t('general/view-store') }}
              </btn>
              <btn
                :href="location.locationLink"
                icon-left="Map/map-pin-2-line">
                {{ $t('general/route-to') }}
              </btn>
            </div>
          </l-popup>
        </l-marker>
      </l-map>
    </client-only>
  </div>
</template>
<script setup>
import {
  LMap,
  LMarker,
  LPopup,
  LTileLayer,
  LIcon
} from '@vue-leaflet/vue-leaflet'
import 'leaflet/dist/leaflet.css'
import bearIcon from '~/assets/static/bear-map-pin.svg?inline'

const config = useRuntimeConfig()

const props = defineProps({
  store: {
    type: String,
    default: ''
  },
  storeDetail: {
    type: Boolean,
    default: false
  },
  stores: {
    type: Array,
    default: null
  },
  initialZoom: {
    type: Number,
    default: 20
  },
  initialCenter: {
    type: Array,
    default: () => [52.351518, 5.598263]
  },
  height: {
    type: String,
    default: ''
  }
})

const center = ref(props.initialCenter)
const map = ref()
const zoom = ref(props.initialZoom)
const iconSize = reactive([50, 50])

const zoomAndShow = (lat, long) => {
  map.value.leafletObject.flyTo([lat + 0.007, long], 14, {
    animate: false,
    duration: 0.9
  })
}

const openPopup = (e, name) => {
  if (props.store === name) {
    setTimeout(() => {
      e.openPopup()
    }, 500)
  }
}

const zoomOut = () => {
  if (map?.value) {
    map.value.leafletObject.flyTo([52.351518, 5.598263], 7, {
      animate: false
    })
  }
}

</script>
<style lang="scss" module>
.vue2leaflet-map {
  z-index: 0;

  &__popup {
    border-radius: 0;

    &__image {
      height: 120px;
      width: 100%;
      object-fit: cover;
    }

    &__text {
      padding: $spacing * 1.5 $spacing * 1.5 $spacing * 1.5 $spacing * 1.5;
      display: flex;
      justify-content: space-between;
      gap: $spacing;

      &--store-detail {
        flex-direction: column;
        align-items: flex-start;
        justify-content: space-around;
        gap: .5rem;
      }
    }

    &__buttons {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      padding: 0 $spacing $spacing * 1.5 $spacing;
      gap: $spacing;

      a:first-child {
        color: $white;
      }

      a:last-child {
        color: $black;
      }
    }
  }
}

:global(.leaflet-popup-content-wrapper) {
  border-radius: 0;
  padding: 0;

  :global(.leaflet-popup-content) {
    width: 385px !important;
    max-height: 280px;
    margin: 0;

    @media (max-width: #{$breakpoint-mobile}) {
      width: 365px !important;
    }

    @media (max-width: #{$breakpoint-tiny}) {
      width: 95vw !important;
    }
  }
}

:global(.leaflet-popup) {
  margin-bottom: 0;
  bottom: 25px;
}

:global(.leaflet-control-attribution) {
  display: none;
}

:global(.leaflet-popup-close-button) {
  margin: $spacing;
  color: $white !important;
  background-color: $black !important;
}

.map {
  height: 500px;

  &--height--400 {
    height: 400px;
  }

  &--height--100vh {
    height: calc(100vh - $spacing * 8);
  }

  &--store {
    :global(.leaflet-popup-content) {
      width: 240px !important;
      margin: 0;
    }

    .vue2leaflet-map {
      z-index: 0;

      &__popup {
        &__buttons {
          a {
            width: 100%;
            margin: 0;
          }
        }
      }
    }
  }
}
</style>
